import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'

import {
    Box,
    FormControl,
    FormHelperText,
    Grid,
    MenuItem,
    Radio,
    RadioGroup,
    FormControlLabel,
    Stack,
    Switch,
    Tooltip,
    Typography,
} from '@mui/material'
import TextField from '@mui/material/TextField'
import Select from '@mui/material/Select'
import { Controller, useController, useForm } from 'react-hook-form'
import { useCheckStudyNumberQuery } from '../../shared/queries/useCheckStudyNumberQuery'
import { useDebounce } from '../../shared/hooks/useDebounce'
import { useGetSettingsQuery } from '../../shared/queries/useGetSettingsQuery'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ActivityStudyField from './ActivityStudyField'
import ActivityDateField from './ActivityDateField'
import ActivityManagementField from './ActivityManagementField'
import ActivityDetailsField from './ActivityDetailsField'
import ActivityBudgetField from './ActivityBudgetField'
import ActivityStatusField from './ActivityStatusField'
import ActivityPublicationField from './ActivityPublicationField'
import { useGetOrganizationQuery } from '../../shared/queries/useGetOrganizationQuery'
import Button from '@mui/material/Button'
import ActivityDeliverablesField from './ActivityDeliverablesField'
import { useStrategy } from '../../shared/hooks/useStrategy'
import { useIsOtsukaOrganization } from '../../shared/hooks/useIsOtsukaOrganization'
import { useGetRequiredFieldsQuery } from '../../shared/queries/useGetRequiredFieldsQuery'
import { getISOStringFromCalendarDate } from '../../utilities/helpers'
import { resolveCountry } from '../../utilities/countryLookup'
import { requiredFieldHelperText } from './requiredFieldHelperText'

dayjs.extend(utc)
dayjs.extend(timezone)

// Mirrors server/validators/activity.js PLANISWARE_PROTECTED_FIELDS exactly — fields owned by
// Planisware once an activity is synced. These are disabled in the UI (see `isPlanisware` below),
// so required-ness can't be enforced on them regardless of the org's required-fields config.
const PLANISWARE_PROTECTED_FIELDS = new Set([
    'studyNumber',
    'title',
    'titleLong',
    'status',
    'type',
    'function',
    'startDate',
    'endDate',
    'plannedStartDate',
    'plannedEndDate',
    'therapeuticArea',
    'compoundName',
    'genericName',
    'indication',
    'affiliateName',
    'providerName',
    'sponsorType',
    'countries',
    'studyReportDate',
    'terminationDate',
    'studyOwner',
    'vendor',
    'vendorDetails',
    'population',
    'externalId',
])

// startDate/endDate live in different form fields depending on quarter-vs-calendar mode
// (quarterOrMonth) — a plain string field in quarter mode, a dayjs-holding "Calendar" field in
// month mode. Every other form field is named identically to its Activity document field.
const getFormFieldName = (documentField: string, quarterOrMonth?: string): string => {
    if (documentField === 'startDate') return quarterOrMonth === 'quarter' ? 'startDate' : 'startDateCalendar'
    if (documentField === 'endDate') return quarterOrMonth === 'quarter' ? 'endDate' : 'endDateCalendar'
    return documentField
}

// Human-readable labels for the summary message shown when a required field is missing.
// Falls back to the raw document field name if a field is required but not listed here.
const REQUIRED_FIELD_LABELS: Record<string, string> = {
    title: 'Short Title',
    titleLong: 'Long Title',
    description: 'Objective',
    status: 'Status',
    type: 'Activity Type',
    category: 'Category',
    function: 'Function',
    studyOwner: 'Study Contact',
    affiliateName: 'Affiliate',
    vendorDetails: 'Vendor Details',
    compoundName: 'Compound',
    externalId: 'External ID',
    studyNumber: 'Study Number',
    sponsorType: 'Sponsor Type',
    providerName: 'Provider Name',
    dataSource: 'Data Source',
    link: 'Link',
    population: 'Population',
    anticipatedMessaging: 'Anticipated Messaging',
    outcomesEndpoints: 'Outcomes/Endpoints',
    countries: 'Countries',
    indication: 'Indication',
    genericName: 'Generic Name',
    therapeuticArea: 'Therapeutic Area',
    startDate: 'Actual Start Date',
    endDate: 'Actual End Date',
    plannedStartDate: 'Planned Start Date',
    plannedEndDate: 'Planned End Date',
    studyReportDate: 'FSR Actual Date',
    terminationDate: 'Terminated On Date',
    resultsAvailableForExternalUseDate: 'Results Available for External Use',
}

const PUBLICATION = {
    isPublication: false,
    Journal: false,
    Congress: false,
    Manuscript: false,
    Presentation: false,
    DataOnFile: false,
}

type ActivityFormMode = 'Edit' | 'Create'

interface ActivityFormProps {
    mode: ActivityFormMode
    onSubmit: (data: any) => void | Promise<void>
    onCancel: () => void
    activity?: any
    deliverables?: any[]
    onNewDeliverable?: () => void
    onDeliverableChange?: (id: string, field: string, value: any) => void
    onDeliverableDelete?: (id: string) => void
    onEditDependencies?: () => void
    onDelete?: () => void
}

const ActivityForm = ({
    mode,
    onSubmit,
    onCancel,
    activity,
    deliverables = [],
    onNewDeliverable,
    onDeliverableChange,
    onDeliverableDelete,
    onEditDependencies,
    onDelete,
}: ActivityFormProps) => {
    const { data: settingsData } = useGetSettingsQuery()
    const organization = useGetOrganizationQuery()
    const strategy = useStrategy()
    const { data: requiredFieldsData } = useGetRequiredFieldsQuery(strategy?.organizationId)

    const { control, handleSubmit, watch, setValue, reset, formState } = useForm({
        // Two-phase required-field UX:
        //   - `mode: 'onSubmit'` — required-ness is only validated when the user hits save, so the
        //     input boxes never turn red just for being empty. Before save, required fields are
        //     signalled solely by the `*` asterisks (driven by `requiredFields`, not validation).
        //   - `reValidateMode: 'onChange'` — once a save has failed, each red box clears live as its
        //     field is filled in, rather than staying red until the next save attempt.
        // Do not switch `mode` to 'onChange'/'onBlur'/'all' — that would turn every required box red
        // on load / first interaction, before the user has tried to save.
        mode: 'onSubmit',
        reValidateMode: 'onChange',
        // We already surface errors via per-field asterisks/helperText and the summary message
        // below, so we don't want RHF's default auto-focus-and-scroll-to-the-first-invalid-field
        // behavior on a failed submit — it jumps the page around.
        shouldFocusError: false,
        defaultValues: {
            title: '',
            titleLong: '',
            description: '',
            budget: 0,
            category: '---',
            function: '---',
            funded: false,
            intendedUse: {} as Record<string, boolean>,
            startDate: '',
            endDate: '',
            startDateCalendar: null as any,
            endDateCalendar: null as any,
            plannedStartDate: null as any,
            plannedEndDate: null as any,
            studyReportDate: null as any,
            terminationDate: null as any,
            resultsAvailableForExternalUseDate: null as any,
            publication: PUBLICATION,
            isPublication: false,
            studyNumber: '',
            dataSource: '',
            vendor: false,
            corporateOrRnDGoal: false,
            vendorDetails: '',
            sponsorType: '',
            affiliateName: '',
            studyOwner: '',
            compoundName: [] as string[],
            countries: [] as string[],
            status: '',
            link: '',
            type: '',
            population: '',
            anticipatedMessaging: '',
            outcomesEndpoints: '',
            externalId: '',
            providerName: '',
            indication: '',
            genericName: [] as string[],
            quarterOrMonth: 'month',
            therapeuticArea: '',
            patientStudy: false,
            processingStatus: null as string | null,
        },
    })

    useEffect(() => {
        if (!activity) return
        reset({
            title: activity.title ?? '',
            titleLong: activity.titleLong ?? '',
            description: activity.description ?? '',
            budget: activity.budget ?? 0,
            category: activity.category ?? '---',
            function: activity.function ?? '---',
            funded: activity.funded ?? false,
            intendedUse: activity.intendedUse ?? {},
            startDate: activity.startDate ?? '',
            endDate: activity.endDate ?? '',
            startDateCalendar: activity.startDate ? dayjs.utc(activity.startDate) : null,
            endDateCalendar: activity.endDate ? dayjs.utc(activity.endDate) : null,
            plannedStartDate: activity.plannedStartDate ? dayjs.utc(activity.plannedStartDate) : null,
            plannedEndDate: activity.plannedEndDate ? dayjs.utc(activity.plannedEndDate) : null,
            studyReportDate: activity.studyReportDate ? dayjs.utc(activity.studyReportDate) : null,
            terminationDate: activity.terminationDate ? dayjs.utc(activity.terminationDate) : null,
            resultsAvailableForExternalUseDate: activity.resultsAvailableForExternalUseDate
                ? dayjs.utc(activity.resultsAvailableForExternalUseDate)
                : null,
            publication: activity.publication ?? PUBLICATION,
            isPublication: activity.publication?.isPublication ?? false,
            studyNumber: activity.studyNumber ?? '',
            dataSource: activity.dataSource ?? '',
            vendor: activity.vendor ?? false,
            corporateOrRnDGoal: activity.corporateOrRnDGoal ?? false,
            vendorDetails: activity.vendorDetails ?? '',
            sponsorType: activity.sponsorType ?? '',
            affiliateName: activity.affiliateName ?? '',
            studyOwner: activity.studyOwner ?? '',
            compoundName: activity.compoundName ?? [],
            countries: activity.countries ?? [],
            status: activity.status ?? '',
            link: activity.link ?? '',
            type: activity.type ?? '',
            population: activity.population ?? '',
            anticipatedMessaging: activity.anticipatedMessaging ?? '',
            outcomesEndpoints: activity.outcomesEndpoints ?? '',
            externalId: activity.externalId ?? '',
            providerName: activity.providerName ?? '',
            indication: activity.indication ?? '',
            genericName: activity.genericName ?? [],
            quarterOrMonth: 'month',
            therapeuticArea: activity.therapeuticArea ?? '',
            patientStudy: activity.patientStudy ?? false,
            processingStatus: activity.processingStatus ?? null,
        })
    }, [activity, reset])

    useEffect(() => {
        if (!settingsData?.activityIntendedUses) return
        const intendedUseObj: Record<string, boolean> = {}
        for (const use of settingsData.activityIntendedUses) {
            intendedUseObj[use] = false
        }
        setValue('intendedUse', intendedUseObj)
    }, [settingsData, setValue])

    const isOtsukaOrganization = useIsOtsukaOrganization()
    // @ts-expect-error redux store's state is not properly typed
    const currentUserName = useSelector((state) => state?.userRecord?.name) ?? ''

    useEffect(() => {
        // For Otsuka orgs, default the Study Contact (studyOwner) to the creating user's name.
        // The user can still edit or clear it before saving.
        if (mode !== 'Create' || !isOtsukaOrganization) return
        setValue('studyOwner', currentUserName)
    }, [mode, isOtsukaOrganization, currentUserName, setValue])

    useEffect(() => {
        // Default a new activity's Indication to the current roadmap's indication.
        // The user can still edit or clear it before saving.
        if (mode !== 'Create' || !strategy?.indication) return
        setValue('indication', strategy.indication)
    }, [mode, strategy?.indication, setValue])

    const studyNumber = watch('studyNumber')
    const debouncedStudyNumber = useDebounce(studyNumber)

    const { data: studyNumberCheck, isFetching: studyNumberChecking } = useCheckStudyNumberQuery({
        studyNumber: debouncedStudyNumber,
        organizationId: organization?.data._id ?? '',
        excludeActivityId: activity?._id,
    })

    const activityCategories: string[] = settingsData?.activityCategories ?? []

    const isPlanisware = activity?.source === 'Planisware'
    const isGslOrPlanisware = activity?.source === 'GSL' || activity?.source === 'Planisware'
    // Once an activity is linked to any source (GSL/Planisware), its study number identifies
    // the external record and can no longer be edited — broader than the full Planisware lock.
    const isStudyNumberLocked = !!activity?.source
    const keyMarketNames = Array.from(
        new Set(
            (strategy?.keyMarkets ?? []).flatMap((phase: any) =>
                (phase.phaseKeyMarkets ?? []).map((m: any) => resolveCountry(m.label || m.code).label).filter(Boolean)
            )
        )
    ) as string[]

    // 'title' is always required, independent of the org's required-fields config — every
    // activity needs a title regardless of whether an admin has explicitly configured it.
    // Required-ness is only enforceable on fields the user can actually edit — skip any
    // field that's currently Planisware-locked, even if it's otherwise required.
    const requiredFields = Array.from(new Set(['title', ...(requiredFieldsData?.activities ?? [])])).filter(
        (field) =>
            !(isPlanisware && PLANISWARE_PROTECTED_FIELDS.has(field)) &&
            !(field === 'studyNumber' && isStudyNumberLocked)
    )

    const quarterOrMonth = watch('quarterOrMonth')

    // `rules` for a field whose required-ness depends on the org's config. Registering this via
    // Controller/useController means RHF itself blocks handleSubmit until it passes, and (per its
    // default onSubmit/onChange revalidation modes) reactively re-checks and clears the error on
    // every subsequent change to that field — no manual error-tracking needed.
    const requiredRule = (documentField: string) => ({
        validate: (value: any) =>
            !requiredFields.includes(documentField) ||
            (Array.isArray(value) ? value.length > 0 : !!value && String(value).trim() !== '') ||
            'Required',
    })
    // startDate/endDate live in different fields depending on quarterOrMonth — only the
    // registration matching the current mode ever enforces required-ness.
    const requiredDateRule = (documentField: string, activeInCurrentMode: boolean) => ({
        validate: (value: any) =>
            !activeInCurrentMode || !requiredFields.includes(documentField) || !!value || 'Required',
    })

    const titleController = useController({ name: 'title', control, rules: requiredRule('title') })
    const titleLongController = useController({ name: 'titleLong', control, rules: requiredRule('titleLong') })
    const descriptionController = useController({ name: 'description', control, rules: requiredRule('description') })
    const activityTypeController = useController({ name: 'type', control, rules: requiredRule('type') })
    const dataSourceController = useController({ name: 'dataSource', control, rules: requiredRule('dataSource') })
    const linkController = useController({ name: 'link', control, rules: requiredRule('link') })
    const populationController = useController({ name: 'population', control, rules: requiredRule('population') })
    const anticipatedMessagingController = useController({
        name: 'anticipatedMessaging',
        control,
        rules: requiredRule('anticipatedMessaging'),
    })
    const outcomesEndpointsController = useController({
        name: 'outcomesEndpoints',
        control,
        rules: requiredRule('outcomesEndpoints'),
    })
    const countriesController = useController({ name: 'countries', control, rules: requiredRule('countries') })
    const compoundNameController = useController({
        name: 'compoundName',
        control,
        rules: requiredRule('compoundName'),
    })
    const indicationController = useController({ name: 'indication', control, rules: requiredRule('indication') })
    const genericNameController = useController({ name: 'genericName', control, rules: requiredRule('genericName') })
    const sponsorTypeController = useController({ name: 'sponsorType', control, rules: requiredRule('sponsorType') })
    const functionController = useController({ name: 'function', control, rules: requiredRule('function') })
    const affiliateNameController = useController({
        name: 'affiliateName',
        control,
        rules: requiredRule('affiliateName'),
    })
    const vendorDetailsController = useController({
        name: 'vendorDetails',
        control,
        rules: requiredRule('vendorDetails'),
    })
    const studyOwnerController = useController({ name: 'studyOwner', control, rules: requiredRule('studyOwner') })
    const providerNameController = useController({
        name: 'providerName',
        control,
        rules: requiredRule('providerName'),
    })
    const plannedStartDateController = useController({
        name: 'plannedStartDate',
        control,
        rules: requiredRule('plannedStartDate'),
    })
    const plannedEndDateController = useController({
        name: 'plannedEndDate',
        control,
        rules: requiredRule('plannedEndDate'),
    })
    const studyReportDateController = useController({
        name: 'studyReportDate',
        control,
        rules: requiredRule('studyReportDate'),
    })
    const terminationDateController = useController({
        name: 'terminationDate',
        control,
        rules: requiredRule('terminationDate'),
    })
    const resultsAvailableForExternalUseDateController = useController({
        name: 'resultsAvailableForExternalUseDate',
        control,
        rules: requiredRule('resultsAvailableForExternalUseDate'),
    })
    const startDateQuarterController = useController({
        name: 'startDate',
        control,
        rules: requiredDateRule('startDate', quarterOrMonth === 'quarter'),
    })
    const startDateCalendarController = useController({
        name: 'startDateCalendar',
        control,
        rules: requiredDateRule('startDate', quarterOrMonth !== 'quarter'),
    })
    const endDateQuarterController = useController({
        name: 'endDate',
        control,
        rules: requiredDateRule('endDate', quarterOrMonth === 'quarter'),
    })
    const endDateCalendarController = useController({
        name: 'endDateCalendar',
        control,
        rules: requiredDateRule('endDate', quarterOrMonth !== 'quarter'),
    })

    // studyNumber, externalId, category, status, and therapeuticArea are registered further down
    // via JSX <Controller> (with `rules={requiredRule(...)}` added there) rather than here, since
    // they're already wired that way for MUI integration — registering them twice would conflict.
    // Every registered field's error lands in formState.errors regardless of which form it was
    // registered through, so this lookup covers both groups uniformly.
    const fieldErrors: Record<string, string> = {}
    for (const requiredField of requiredFields) {
        const formField = getFormFieldName(requiredField, quarterOrMonth)
        const message = (formState.errors as any)[formField]?.message
        if (message) fieldErrors[requiredField] = message
    }

    const missingRequiredFieldLabels = Object.keys(fieldErrors).map((field) => REQUIRED_FIELD_LABELS[field] ?? field)

    const buildActivityPayload = (data: any) => ({
        studyNumber: data.studyNumber,
        title: data.title,
        titleLong: data.titleLong,
        description: data.description,
        dataSource: data.dataSource,
        budget: data.budget ? data.budget : 0,
        category: data.category,
        function: data.function,
        intendedUse: data.intendedUse,
        funded: data.funded,
        startDate:
            data.quarterOrMonth === 'quarter' ? data.startDate : getISOStringFromCalendarDate(data.startDateCalendar),
        endDate: data.quarterOrMonth === 'quarter' ? data.endDate : getISOStringFromCalendarDate(data.endDateCalendar),
        plannedStartDate: getISOStringFromCalendarDate(data.plannedStartDate),
        plannedEndDate: getISOStringFromCalendarDate(data.plannedEndDate),
        studyReportDate: getISOStringFromCalendarDate(data.studyReportDate),
        terminationDate: getISOStringFromCalendarDate(data.terminationDate),
        resultsAvailableForExternalUseDate: getISOStringFromCalendarDate(data.resultsAvailableForExternalUseDate),
        status: data.status,
        publication: {
            isPublication: data.isPublication,
            Journal: data.publication.Journal,
            Congress: data.publication.Congress,
            Manuscript: data.publication.Manuscript,
            Presentation: data.publication.Presentation,
            DataOnFile: data.publication.DataOnFile,
        },
        vendor: data.vendor,
        corporateOrRnDGoal: data.corporateOrRnDGoal,
        vendorDetails: data.vendorDetails,
        link: data.link,
        type: data.type,
        population: data.population,
        anticipatedMessaging: data.anticipatedMessaging,
        outcomesEndpoints: data.outcomesEndpoints,
        indication: data.indication,
        therapeuticArea: data.therapeuticArea,
        genericName: data.genericName,
        compoundName: data.compoundName,
        affiliateName: data.affiliateName,
        providerName: data.providerName,
        sponsorType: data.sponsorType,
        countries: data.countries,
        studyOwner: data.studyOwner,
        externalId: data.externalId,
        patientStudy: data.patientStudy,
        processingStatus: data.processingStatus,
        ...(mode === 'Edit'
            ? {
                  strategyId: activity.strategyId,
                  organizationId: activity.organizationId,
                  linkedEvidence: activity.linkedEvidence,
                  linkedValueMessages: activity.linkedValueMessages,
                  primaryEvidence: activity.primaryEvidence,
                  activityDependencies: activity.activityDependencies ?? [],
                  // deliverables is intentionally NOT included here — it's already managed by
                  // the dedicated create/delete deliverable endpoints ($push/$pull server-side).
                  // Echoing back activity.deliverables would send a stale snapshot and, since
                  // the main save does a $set, silently undo any add/remove made during this
                  // same edit session.
                  order: activity.order,
              }
            : {
                  strategyId: strategy?._id,
                  organizationId: strategy?.organizationId,
                  linkedEvidence: [],
                  linkedValueMessages: [],
                  primaryEvidence: [],
                  activityDependencies: [],
                  order: 0,
              }),
    })

    return (
        <form
            onSubmit={handleSubmit(async (data) => {
                await onSubmit(buildActivityPayload(data))
            })}
        >
            <Box
                sx={{
                    px: 4,
                    py: 3,
                    // Thicken the red error outline on invalid required fields so a failed submit is
                    // easier to spot. Scoped to this form's outlined inputs (TextField, Select, date
                    // pickers, autocomplete all render the notched outline in their error state).
                    '& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline': {
                        borderWidth: '2px',
                    },
                    // Make disabled Planisware fields obvious via a scoped tinted background +
                    // not-allowed cursor on this wrapper, instead of per-field overrides.
                    ...(isPlanisware && {
                        '& .Mui-disabled': {
                            cursor: 'not-allowed !important',
                        },
                        '& .MuiOutlinedInput-root.Mui-disabled': {
                            backgroundColor: 'figma.primary3',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: 'transparent',
                            },
                        },
                        '& .MuiInputBase-input.Mui-disabled, & .MuiSelect-select.Mui-disabled, & .MuiFormLabel-root.Mui-disabled':
                            {
                                WebkitTextFillColor: 'unset',
                                color: 'figma.grayscale.textHier1',
                                opacity: 0.9,
                            },
                    }),
                }}
            >
                {isPlanisware && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            backgroundColor: 'figma.primary3',
                            borderRadius: '8px',
                            px: 2,
                            py: 1,
                            mb: 3,
                        }}
                    >
                        <InfoOutlinedIcon sx={{ fontSize: 16, color: 'figma.primary', flexShrink: 0 }} />
                        <Typography variant="14x400" sx={{ color: 'figma.grayscale.textHier1' }}>
                            Fields synchronized from Planisware are read-only in ATLAS to maintain source alignment.
                        </Typography>
                    </Box>
                )}
                <Grid container direction="column" spacing={1}>
                    <Grid item>
                        <Stack spacing={2}>
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                                        Study Number
                                        {requiredFields.includes('studyNumber') && (
                                            <Typography component="span" sx={{ color: 'error.main' }}>
                                                {' '}
                                                *
                                            </Typography>
                                        )}
                                    </Typography>
                                    <Controller
                                        name="studyNumber"
                                        control={control}
                                        rules={requiredRule('studyNumber')}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                placeholder="Enter Study Number"
                                                disabled={isStudyNumberLocked}
                                                onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                                                error={
                                                    (!isStudyNumberLocked &&
                                                        !studyNumberChecking &&
                                                        !!studyNumber &&
                                                        studyNumberCheck?.exists) ||
                                                    !!fieldErrors.studyNumber
                                                }
                                                helperText={
                                                    fieldErrors.studyNumber ? (
                                                        fieldErrors.studyNumber
                                                    ) : !isStudyNumberLocked && studyNumber && !studyNumberChecking ? (
                                                        studyNumberCheck?.exists ? (
                                                            'Study number already exists'
                                                        ) : (
                                                            <Box
                                                                component="span"
                                                                sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                                                            >
                                                                <CheckCircleOutlineIcon
                                                                    sx={{ fontSize: 14, color: 'success.main' }}
                                                                />
                                                                Study number is available
                                                            </Box>
                                                        )
                                                    ) : requiredFields.includes('studyNumber') ? (
                                                        <Box component="span" sx={{ color: 'error.main' }}>
                                                            Required
                                                        </Box>
                                                    ) : (
                                                        ' '
                                                    )
                                                }
                                                FormHelperTextProps={{
                                                    sx: {
                                                        // Only the "study number is available" message is green.
                                                        // The neutral 'Required' hint (shown when the field is
                                                        // empty) must stay muted, so gate green on there being a
                                                        // typed, checked, non-existing study number.
                                                        color:
                                                            !isStudyNumberLocked &&
                                                            studyNumber &&
                                                            !studyNumberChecking &&
                                                            !fieldErrors.studyNumber &&
                                                            !studyNumberCheck?.exists
                                                                ? 'success.main'
                                                                : undefined,
                                                    },
                                                }}
                                                sx={{
                                                    '.MuiInputBase-root': { borderRadius: '12px' },
                                                    // Match the Planisware read-only field treatment when only this
                                                    // field is locked (GSL-sourced activities aren't fully Planisware
                                                    // -disabled, so the wrapper-level styling doesn't reach it).
                                                    ...(isStudyNumberLocked &&
                                                        !isPlanisware && {
                                                            '& .Mui-disabled': {
                                                                cursor: 'not-allowed !important',
                                                            },
                                                            '& .MuiOutlinedInput-root.Mui-disabled': {
                                                                backgroundColor: 'figma.primary3',
                                                                '& .MuiOutlinedInput-notchedOutline': {
                                                                    borderColor: 'transparent',
                                                                },
                                                            },
                                                            '& .MuiInputBase-input.Mui-disabled': {
                                                                WebkitTextFillColor: 'unset',
                                                                color: 'figma.grayscale.textHier1',
                                                                opacity: 0.9,
                                                            },
                                                        }),
                                                }}
                                            />
                                        )}
                                    />
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                                        External ID
                                        {requiredFields.includes('externalId') && (
                                            <Typography component="span" sx={{ color: 'error.main' }}>
                                                {' '}
                                                *
                                            </Typography>
                                        )}
                                    </Typography>
                                    <Controller
                                        name="externalId"
                                        control={control}
                                        rules={requiredRule('externalId')}
                                        render={({ field }) => (
                                            <TextField
                                                {...field}
                                                fullWidth
                                                placeholder="Enter External ID"
                                                disabled={isPlanisware}
                                                error={!!fieldErrors.externalId}
                                                helperText={requiredFieldHelperText(
                                                    'externalId',
                                                    requiredFields,
                                                    fieldErrors
                                                )}
                                                onFocus={(e) => e.target.setAttribute('autocomplete', 'off')}
                                                sx={{ '.MuiInputBase-root': { borderRadius: '12px' } }}
                                            />
                                        )}
                                    />
                                </Box>
                            </Box>
                            {isGslOrPlanisware && (
                                <Box>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                                        <Typography variant="16x600">Processing Status</Typography>
                                        <Tooltip
                                            title="Controls visibility in the imports tab. 'Processing' activities appear in the imports tab for review. Once set to 'Processed' they will be removed from the imports tab."
                                            arrow
                                        >
                                            <InfoOutlinedIcon
                                                sx={{ fontSize: 16, color: 'figma.grayscale.tier3', cursor: 'help' }}
                                            />
                                        </Tooltip>
                                    </Box>
                                    <Controller
                                        name="processingStatus"
                                        control={control}
                                        render={({ field }) => (
                                            <RadioGroup
                                                row
                                                value={field.value}
                                                onChange={(e) => field.onChange(e.target.value)}
                                            >
                                                <FormControlLabel
                                                    value="processing"
                                                    control={<Radio />}
                                                    label="Processing"
                                                />
                                                <FormControlLabel
                                                    value="processed"
                                                    control={<Radio />}
                                                    label="Processed"
                                                />
                                            </RadioGroup>
                                        )}
                                    />
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                                        Category
                                        {requiredFields.includes('category') && (
                                            <Typography component="span" sx={{ color: 'error.main' }}>
                                                {' '}
                                                *
                                            </Typography>
                                        )}
                                    </Typography>
                                    <FormControl fullWidth error={!!fieldErrors.category}>
                                        <Controller
                                            name="category"
                                            control={control}
                                            rules={requiredRule('category')}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    displayEmpty
                                                    error={!!fieldErrors.category}
                                                    renderValue={
                                                        field.value && field.value !== '---'
                                                            ? undefined
                                                            : () => (
                                                                  <Typography
                                                                      variant="16x600"
                                                                      component="span"
                                                                      sx={{ color: 'figma.primary' }}
                                                                  >
                                                                      Select Category
                                                                  </Typography>
                                                              )
                                                    }
                                                    sx={{
                                                        '& .MuiOutlinedInput-notchedOutline': { borderRadius: '12px' },
                                                    }}
                                                >
                                                    <MenuItem value={'---'}>---</MenuItem>
                                                    {activityCategories.sort().map((category, i) => (
                                                        <MenuItem value={category} key={category + i}>
                                                            {category}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            )}
                                        />
                                        <FormHelperText>
                                            {requiredFieldHelperText('category', requiredFields, fieldErrors)}
                                        </FormHelperText>
                                    </FormControl>
                                </Box>
                                <Box sx={{ flex: 1 }}>
                                    <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                                        Status
                                        {requiredFields.includes('status') && (
                                            <Typography component="span" sx={{ color: 'error.main' }}>
                                                {' '}
                                                *
                                            </Typography>
                                        )}
                                    </Typography>
                                    <Controller
                                        name="status"
                                        control={control}
                                        rules={requiredRule('status')}
                                        render={({ field }) => (
                                            <ActivityStatusField
                                                value={field.value}
                                                onChange={field.onChange}
                                                disabled={isPlanisware}
                                                existingValue={activity?.status}
                                                error={!!fieldErrors.status}
                                                helperText={fieldErrors.status}
                                                required={requiredFields.includes('status')}
                                            />
                                        )}
                                    />
                                </Box>
                            </Box>
                        </Stack>
                    </Grid>
                    <Grid item>
                        <ActivityStudyField
                            shortTitle={titleController.field.value}
                            onShortTitleChange={titleController.field.onChange}
                            longTitle={titleLongController.field.value}
                            onLongTitleChange={titleLongController.field.onChange}
                            objective={descriptionController.field.value}
                            onObjectiveChange={descriptionController.field.onChange}
                            disabled={isPlanisware}
                            requiredFields={requiredFields}
                            fieldErrors={fieldErrors}
                        />
                    </Grid>
                    <Grid item>
                        <ActivityDateField
                            mode={watch('quarterOrMonth')}
                            onModeChange={(val) => setValue('quarterOrMonth', val)}
                            quarterOldStart={null}
                            quarterOldEnd={null}
                            onQuarterStartChange={startDateQuarterController.field.onChange}
                            onQuarterEndChange={endDateQuarterController.field.onChange}
                            startDate={startDateCalendarController.field.value}
                            endDate={endDateCalendarController.field.value}
                            onStartDateChange={startDateCalendarController.field.onChange}
                            onEndDateChange={endDateCalendarController.field.onChange}
                            plannedStartDate={plannedStartDateController.field.value}
                            plannedEndDate={plannedEndDateController.field.value}
                            onPlannedStartDateChange={plannedStartDateController.field.onChange}
                            onPlannedEndDateChange={plannedEndDateController.field.onChange}
                            studyReportDate={studyReportDateController.field.value}
                            onStudyReportDateChange={studyReportDateController.field.onChange}
                            terminationDate={terminationDateController.field.value}
                            onTerminationDateChange={terminationDateController.field.onChange}
                            resultsAvailableForExternalUseDate={
                                resultsAvailableForExternalUseDateController.field.value
                            }
                            onResultsAvailableForExternalUseDateChange={
                                resultsAvailableForExternalUseDateController.field.onChange
                            }
                            disabled={isPlanisware}
                            clearable
                            requiredFields={requiredFields}
                            fieldErrors={fieldErrors}
                        />
                    </Grid>
                    <Grid item>
                        <ActivityManagementField
                            sponsorType={sponsorTypeController.field.value}
                            onSponsorTypeChange={sponsorTypeController.field.onChange}
                            isFunction={functionController.field.value}
                            onFunctionChange={functionController.field.onChange}
                            affiliateName={affiliateNameController.field.value}
                            onAffiliateNameChange={affiliateNameController.field.onChange}
                            isVendor={watch('vendor')}
                            onIsVendorChange={(val) => setValue('vendor', val)}
                            vendorDetails={vendorDetailsController.field.value}
                            onVendorDetailsChange={vendorDetailsController.field.onChange}
                            corporateOrRnDGoal={watch('corporateOrRnDGoal')}
                            onCorporateOrRnDGoalChange={(val) => setValue('corporateOrRnDGoal', val)}
                            studyOwner={studyOwnerController.field.value}
                            onStudyOwnerChange={studyOwnerController.field.onChange}
                            providerName={providerNameController.field.value}
                            onProviderNameChange={providerNameController.field.onChange}
                            existingFunction={activity?.function}
                            existingSponsorType={activity?.sponsorType}
                            disabled={isPlanisware}
                            requiredFields={requiredFields}
                            fieldErrors={fieldErrors}
                        />
                    </Grid>
                    <Grid item>
                        <ActivityDetailsField
                            activityType={activityTypeController.field.value}
                            onActivityTypeChange={activityTypeController.field.onChange}
                            population={populationController.field.value}
                            onPopulationChange={populationController.field.onChange}
                            dataSource={dataSourceController.field.value}
                            onDataSourceChange={dataSourceController.field.onChange}
                            link={linkController.field.value}
                            onLinkChange={linkController.field.onChange}
                            anticipatedMessaging={anticipatedMessagingController.field.value}
                            onAnticipatedMessagingChange={anticipatedMessagingController.field.onChange}
                            outcomesEndpoints={outcomesEndpointsController.field.value}
                            onOutcomesEndpointsChange={outcomesEndpointsController.field.onChange}
                            intendedUse={watch('intendedUse')}
                            onIntendedUseChange={(key, checked) =>
                                setValue('intendedUse', { ...watch('intendedUse'), [key]: checked })
                            }
                            countries={countriesController.field.value}
                            onCountriesChange={countriesController.field.onChange}
                            compoundName={compoundNameController.field.value}
                            onCompoundNameChange={compoundNameController.field.onChange}
                            indication={indicationController.field.value}
                            onIndicationChange={indicationController.field.onChange}
                            genericName={genericNameController.field.value}
                            onGenericNameChange={genericNameController.field.onChange}
                            keyMarketNames={keyMarketNames}
                            existingActivityType={activity?.type}
                            existingPopulation={activity?.population}
                            disabled={isPlanisware}
                            requiredFields={requiredFields}
                            fieldErrors={fieldErrors}
                        />
                    </Grid>
                    <Grid item>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                            <Box sx={{ flex: 1 }}>
                                <Typography variant="16x600" sx={{ mb: 1, display: 'block' }}>
                                    Therapeutic Area
                                    {requiredFields.includes('therapeuticArea') && (
                                        <Typography component="span" sx={{ color: 'error.main' }}>
                                            {' '}
                                            *
                                        </Typography>
                                    )}
                                </Typography>
                                <Controller
                                    name="therapeuticArea"
                                    control={control}
                                    rules={requiredRule('therapeuticArea')}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            fullWidth
                                            placeholder="Enter Therapeutic Area"
                                            error={!!fieldErrors.therapeuticArea}
                                            helperText={requiredFieldHelperText(
                                                'therapeuticArea',
                                                requiredFields,
                                                fieldErrors
                                            )}
                                            sx={{ '.MuiInputBase-root': { borderRadius: '12px' } }}
                                        />
                                    )}
                                />
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 3 }}>
                                <Typography variant="16x600">Patient Study</Typography>
                                <Controller
                                    name="patientStudy"
                                    control={control}
                                    render={({ field }) => (
                                        <Switch
                                            checked={field.value}
                                            onChange={(e) => field.onChange(e.target.checked)}
                                        />
                                    )}
                                />
                            </Box>
                        </Box>
                    </Grid>
                    <Grid container item spacing={1} alignContent="flex-start">
                        <Grid container item direction="column" xs>
                            <Controller
                                name="isPublication"
                                control={control}
                                render={({ field: isPublicationField }) => (
                                    <Controller
                                        name="publication"
                                        control={control}
                                        render={({ field: publicationField }) => (
                                            <ActivityPublicationField
                                                isPublication={isPublicationField.value}
                                                onIsPublicationChange={isPublicationField.onChange}
                                                publication={publicationField.value}
                                                onPublicationChange={(key, checked) =>
                                                    publicationField.onChange({
                                                        ...publicationField.value,
                                                        [key]: checked,
                                                    })
                                                }
                                            />
                                        )}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>
                    <Grid item>
                        <Controller
                            name="budget"
                            control={control}
                            render={({ field: budgetField }) => (
                                <Controller
                                    name="funded"
                                    control={control}
                                    render={({ field: fundedField }) => (
                                        <ActivityBudgetField
                                            budget={budgetField.value}
                                            onBudgetChange={budgetField.onChange}
                                            funded={fundedField.value}
                                            onFundedChange={fundedField.onChange}
                                        />
                                    )}
                                />
                            )}
                        />
                    </Grid>
                    {mode === 'Edit' && (
                        <Grid item>
                            <Grid container direction="column" spacing={2} sx={{ mb: 2 }}>
                                <Grid item>
                                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                        Dependencies
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Button variant="contained" onClick={() => onEditDependencies?.()}>
                                        Edit Dependencies
                                    </Button>
                                </Grid>
                            </Grid>
                            <ActivityDeliverablesField
                                deliverables={deliverables}
                                onNewDeliverable={onNewDeliverable}
                                onDeliverableChange={onDeliverableChange}
                                onDeliverableDelete={onDeliverableDelete}
                            />
                        </Grid>
                    )}
                </Grid>
                <Box sx={{ mt: 2 }}>
                    <Typography
                        variant="body2"
                        sx={{ color: 'error.main', mb: 1, textAlign: 'right', minHeight: '1.25em' }}
                    >
                        {missingRequiredFieldLabels.length > 0 &&
                            `Missing required field${missingRequiredFieldLabels.length > 1 ? 's' : ''}: ${missingRequiredFieldLabels.join(', ')}`}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                        {mode === 'Edit' && onDelete && (
                            <Button
                                variant="text"
                                onClick={onDelete}
                                disabled={formState.isSubmitting}
                                sx={{ minWidth: 110 }}
                            >
                                Delete
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            onClick={() => {
                                reset()
                                onCancel()
                            }}
                            disabled={formState.isSubmitting}
                            sx={{ minWidth: 110 }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={formState.isSubmitting}
                            sx={{ minWidth: 110 }}
                        >
                            {mode === 'Edit' ? 'Save' : 'Create'}
                        </Button>
                    </Box>
                </Box>
            </Box>
        </form>
    )
}

export default ActivityForm
